import { useEffect, useRef, useState, type FormEvent } from 'react'
import { Navigate } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { Bot, MessageSquare, Rocket, Search, Sparkles, User } from 'lucide-react'
import { AppChrome } from '../components/AppChrome'
import { useAuth } from '../lib/auth'

type Intent = 'new_app' | 'bug_fix' | 'feature'

type ChatMessage = {
  role: string
  kind?: string
  content: string
  at?: string
  slot?: string
}

type Session = {
  interview_id: string
  intent: Intent
  status: string
  progress_pct: number
  pulse?: string
  ready_for_handoff?: boolean
  messages: ChatMessage[]
  research?: { hook: string; summary: string; findings: string[] }[]
  handoff_id?: string
  build_id?: string
  next_prompt?: string | null
}

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) },
    ...init,
  })
  if (!res.ok) throw new Error(await res.text())
  return res.json() as Promise<T>
}

const INTENTS: { id: Intent; title: string; blurb: string }[] = [
  {
    id: 'new_app',
    title: 'New app',
    blurb: 'Greenfield product — job-to-be-done through constraints',
  },
  {
    id: 'bug_fix',
    title: 'Bug fix',
    blurb: 'Symptoms, repro, evidence, and acceptance for the fix',
  },
  {
    id: 'feature',
    title: 'New feature',
    blurb: 'Story, surfaces, scope, and shippable acceptance',
  },
]

export function InterviewPage() {
  const { user, loading } = useAuth()
  const [intent, setIntent] = useState<Intent>('new_app')
  const [seed, setSeed] = useState('')
  const [session, setSession] = useState<Session | null>(null)
  const [draft, setDraft] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [handoffNote, setHandoffNote] = useState<string | null>(null)
  const bottomRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [session?.messages?.length, busy])

  if (!loading && !user) return <Navigate to="/signin" replace />

  async function startInterview(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setHandoffNote(null)
    setBusy(true)
    try {
      const row = await api<Session>('/api/afos360/v1/interview/start', {
        method: 'POST',
        body: JSON.stringify({ intent, seed: seed || null }),
      })
      setSession(row)
      setDraft('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Start failed')
    } finally {
      setBusy(false)
    }
  }

  async function sendTurn(e: FormEvent) {
    e.preventDefault()
    if (!session || !draft.trim()) return
    setError(null)
    setBusy(true)
    const message = draft.trim()
    setDraft('')
    try {
      const row = await api<Session>(
        `/api/afos360/v1/interview/${encodeURIComponent(session.interview_id)}/turn`,
        { method: 'POST', body: JSON.stringify({ message }) },
      )
      setSession(row)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Turn failed')
      setDraft(message)
    } finally {
      setBusy(false)
    }
  }

  async function doHandoff() {
    if (!session) return
    setBusy(true)
    setError(null)
    try {
      const result = await api<{
        handoff_id: string
        build_id?: string
        discovery_id?: string
        session: Session
      }>(`/api/afos360/v1/interview/${encodeURIComponent(session.interview_id)}/handoff`, {
        method: 'POST',
        body: JSON.stringify({ run_design_phases: true, mode: 'dry_run' }),
      })
      setSession(result.session)
      setHandoffNote(
        `Handed off ${result.handoff_id} → definitions & architecture` +
          (result.build_id ? ` · build ${result.build_id}` : ''),
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Handoff failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <AppChrome>
      <main className="afos-page grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="lg:col-span-2"
        >
          <h1 className="afos-page-title">Interview agent</h1>
          <p className="afos-page-lead">
            Talk through a new app, bug fix, or feature. I research as we go, then hand a
            requirements packet to the definitions & architecture agents.
          </p>
        </motion.section>

        {!session && (
          <motion.form
            onSubmit={startInterview}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4 rounded-3xl border border-[var(--color-line)] bg-black/30 p-6 lg:col-span-2"
          >
            <div className="grid gap-3 md:grid-cols-3">
              {INTENTS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setIntent(item.id)}
                  className={
                    'rounded-2xl border px-4 py-4 text-left transition ' +
                    (intent === item.id
                      ? 'border-[var(--color-cyan)] bg-[rgba(46,230,197,0.1)]'
                      : 'border-[var(--color-line)] bg-black/20 hover:border-[var(--color-mist)]')
                  }
                >
                  <div className="font-semibold">{item.title}</div>
                  <div className="mt-1 text-xs text-[var(--color-mist)]">{item.blurb}</div>
                </button>
              ))}
            </div>
            <textarea
              className="min-h-28 w-full rounded-2xl border border-[var(--color-line)] bg-black/30 px-4 py-3 outline-none focus:border-[var(--color-cyan)]"
              placeholder="Optional seed — idea, bug symptom, or feature story"
              value={seed}
              onChange={(e) => setSeed(e.target.value)}
            />
            <button
              type="submit"
              disabled={busy}
              className="inline-flex items-center gap-2 rounded-full bg-[var(--color-foam)] px-6 py-3 font-semibold text-[var(--color-void)]"
            >
              <Sparkles className="h-4 w-4" />
              Start interview
            </button>
            {error && <p className="text-sm text-[var(--color-rose)]">{error}</p>}
          </motion.form>
        )}

        {session && (
          <>
            <section className="flex min-h-[32rem] flex-col rounded-3xl border border-[var(--color-line)] bg-black/30">
              <div className="flex items-center justify-between border-b border-[var(--color-line)] px-5 py-3">
                <div className="flex items-center gap-2 text-sm">
                  <MessageSquare className="h-4 w-4 text-[var(--color-cyan)]" />
                  <span className="font-[family-name:var(--font-mono)] text-xs text-[var(--color-mist)]">
                    {session.interview_id}
                  </span>
                </div>
                <div className="text-xs text-[var(--color-mist)]">
                  {session.progress_pct}% · {session.pulse}
                </div>
              </div>
              <div className="h-2 w-full bg-black/40">
                <motion.div
                  className="h-full bg-[var(--color-cyan)]"
                  initial={false}
                  animate={{ width: `${session.progress_pct}%` }}
                  transition={{ type: 'spring', stiffness: 120, damping: 20 }}
                />
              </div>
              <div className="flex-1 space-y-3 overflow-y-auto px-5 py-4">
                <AnimatePresence initial={false}>
                  {session.messages.map((msg, i) => (
                    <motion.div
                      key={`${msg.at}-${i}`}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={
                        'flex gap-3 ' + (msg.role === 'user' ? 'justify-end' : 'justify-start')
                      }
                    >
                      {msg.role !== 'user' && (
                        <div className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[var(--color-line)] bg-black/40">
                          {msg.kind === 'research' ? (
                            <Search className="h-4 w-4 text-[var(--color-amber)]" />
                          ) : (
                            <Bot className="h-4 w-4 text-[var(--color-cyan)]" />
                          )}
                        </div>
                      )}
                      <div
                        className={
                          'max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-3 text-sm leading-relaxed ' +
                          (msg.role === 'user'
                            ? 'bg-[var(--color-foam)] text-[var(--color-void)]'
                            : msg.kind === 'research'
                              ? 'border border-[rgba(255,179,71,0.35)] bg-[rgba(255,179,71,0.08)]'
                              : msg.kind === 'handoff'
                                ? 'border border-[rgba(46,230,197,0.4)] bg-[rgba(46,230,197,0.1)]'
                                : 'border border-[var(--color-line)] bg-black/35')
                        }
                      >
                        {msg.kind && msg.role !== 'user' && (
                          <div className="mb-1 text-[10px] uppercase tracking-wider text-[var(--color-mist)]">
                            {msg.kind}
                          </div>
                        )}
                        {msg.content}
                      </div>
                      {msg.role === 'user' && (
                        <div className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[var(--color-line)] bg-black/40">
                          <User className="h-4 w-4" />
                        </div>
                      )}
                    </motion.div>
                  ))}
                </AnimatePresence>
                {busy && (
                  <div className="text-xs text-[var(--color-mist)]">Agent thinking…</div>
                )}
                <div ref={bottomRef} />
              </div>
              {session.status !== 'handed_off' && !session.ready_for_handoff && (
                <form
                  onSubmit={sendTurn}
                  className="border-t border-[var(--color-line)] p-4"
                >
                  <div className="flex gap-2">
                    <input
                      className="flex-1 rounded-2xl border border-[var(--color-line)] bg-black/30 px-4 py-3 outline-none focus:border-[var(--color-cyan)]"
                      placeholder="Type your answer…"
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      disabled={busy}
                    />
                    <button
                      type="submit"
                      disabled={busy || !draft.trim()}
                      className="rounded-full bg-[var(--color-foam)] px-5 py-3 font-semibold text-[var(--color-void)] disabled:opacity-40"
                    >
                      Send
                    </button>
                  </div>
                </form>
              )}
              {session.ready_for_handoff && session.status !== 'handed_off' && (
                <div className="border-t border-[var(--color-line)] p-4">
                  <button
                    type="button"
                    onClick={() => void doHandoff()}
                    disabled={busy}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[var(--color-cyan)] px-5 py-3 font-semibold text-[var(--color-void)]"
                  >
                    <Rocket className="h-4 w-4" />
                    Hand off to design & architecture
                  </button>
                </div>
              )}
            </section>

            <aside className="space-y-4">
              <div className="rounded-3xl border border-[var(--color-line)] bg-black/30 p-5">
                <h2 className="font-[family-name:var(--font-display)] text-lg font-bold">
                  Live status
                </h2>
                <dl className="mt-3 space-y-2 text-sm">
                  <div className="flex justify-between gap-3">
                    <dt className="text-[var(--color-mist)]">Intent</dt>
                    <dd>{session.intent}</dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-[var(--color-mist)]">Status</dt>
                    <dd>{session.status}</dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-[var(--color-mist)]">Progress</dt>
                    <dd>{session.progress_pct}%</dd>
                  </div>
                  {session.handoff_id && (
                    <div className="flex justify-between gap-3">
                      <dt className="text-[var(--color-mist)]">Handoff</dt>
                      <dd className="font-[family-name:var(--font-mono)] text-xs">
                        {session.handoff_id}
                      </dd>
                    </div>
                  )}
                  {session.build_id && (
                    <div className="flex justify-between gap-3">
                      <dt className="text-[var(--color-mist)]">Build</dt>
                      <dd className="font-[family-name:var(--font-mono)] text-xs">
                        {session.build_id}
                      </dd>
                    </div>
                  )}
                </dl>
                {handoffNote && (
                  <p className="mt-3 text-sm text-[var(--color-cyan)]">{handoffNote}</p>
                )}
                {error && <p className="mt-3 text-sm text-[var(--color-rose)]">{error}</p>}
              </div>

              <div className="rounded-3xl border border-[var(--color-line)] bg-black/30 p-5">
                <h2 className="flex items-center gap-2 font-[family-name:var(--font-display)] text-lg font-bold">
                  <Search className="h-4 w-4 text-[var(--color-amber)]" />
                  Research pulses
                </h2>
                <div className="mt-3 space-y-3">
                  {(session.research || []).length === 0 && (
                    <p className="text-sm text-[var(--color-mist)]">
                      Research appears between questions as we learn more.
                    </p>
                  )}
                  {(session.research || []).map((row) => (
                    <div
                      key={row.hook + row.summary.slice(0, 24)}
                      className="rounded-2xl border border-[rgba(255,179,71,0.25)] bg-[rgba(255,179,71,0.06)] p-3 text-xs"
                    >
                      <div className="mb-1 font-semibold uppercase tracking-wider text-[var(--color-amber)]">
                        {row.hook.replace(/_/g, ' ')}
                      </div>
                      <ul className="space-y-1 text-[var(--color-mist)]">
                        {(row.findings || []).slice(0, 4).map((f) => (
                          <li key={f}>· {f}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>

              <button
                type="button"
                className="text-sm text-[var(--color-mist)] underline"
                onClick={() => {
                  setSession(null)
                  setHandoffNote(null)
                  setError(null)
                }}
              >
                Start another interview
              </button>
            </aside>
          </>
        )}
      </main>
    </AppChrome>
  )
}
